import html2canvas from 'html2canvas';
import { uploadBlobAndGetUrl } from './storage';

/**
 * Генерирует изображение сертификата из HTML элемента
 * @param element - HTML элемент с сертификатом
 * @returns Data URL изображения в формате PNG
 */
export async function generateCertificateImage(
  element: HTMLElement
): Promise<string> {
  try {
    console.log('Generating certificate image from element:', element);
    console.log('Element dimensions:', {
      width: element.offsetWidth,
      height: element.offsetHeight,
      scrollWidth: element.scrollWidth,
      scrollHeight: element.scrollHeight,
    });

    // Ждем загрузки всех изображений и шрифтов
    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(element, {
      scale: 1, // Уменьшаем scale для стабильности
      useCORS: true,
      backgroundColor: '#151515',
      logging: true, // Включаем логирование для отладки
      allowTaint: true,
      foreignObjectRendering: false, // Отключаем для стабильности
      removeContainer: false,
      imageTimeout: 5000,
      onclone: (clonedDoc) => {
        console.log('Cloned document created');
        // Убеждаемся, что все стили применены
        const clonedElement = clonedDoc.querySelector(
          '#certificate-print-root'
        );
        if (clonedElement) {
          console.log('Cloned element found:', clonedElement);
        }
      },
    });

    console.log('Canvas generated:', {
      width: canvas.width,
      height: canvas.height,
    });

    // Проверяем, что canvas не пустой
    const ctx = canvas.getContext('2d');
    const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
    const hasContent = imageData?.data.some((pixel) => pixel !== 0);

    if (!hasContent) {
      console.warn('Canvas appears to be empty, trying alternative approach');

      // Альтернативный подход с другими настройками
      const canvas2 = await html2canvas(element, {
        scale: 1,
        useCORS: true,
        backgroundColor: '#151515',
        logging: true,
        allowTaint: true,
        foreignObjectRendering: true,
        width: element.scrollWidth || element.offsetWidth,
        height: element.scrollHeight || element.offsetHeight,
      });

      const dataUrl = canvas2.toDataURL('image/png', 0.95);
      console.log('Alternative canvas generated:', {
        width: canvas2.width,
        height: canvas2.height,
        dataUrlLength: dataUrl.length,
      });

      return dataUrl;
    }

    const dataUrl = canvas.toDataURL('image/png', 0.95);
    console.log('Image generated successfully:', {
      dataUrlLength: dataUrl.length,
      firstChars: dataUrl.substring(0, 50),
    });

    // Проверяем, что изображение сгенерировалось корректно
    if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 1000) {
      throw new Error('Failed to generate valid image data URL');
    }

    return dataUrl;
  } catch (error) {
    console.error('Error generating certificate image:', error);
    throw new Error(
      `Certificate image generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Загружает изображение в AWS S3 и возвращает публичную ссылку
 * @param blob - Blob изображения
 * @param lang - Код языка (en|ru) для метаданных
 * @returns Публичная ссылка на изображение
 */
export async function uploadImageAndGetUrl(
  blob: Blob,
  lang?: string
): Promise<string> {
  console.log('Uploading image to S3, blob size:', blob.size, 'lang:', lang);

  try {
    const fileName = `certificate-${Date.now()}.png`;
    const url = await uploadBlobAndGetUrl({
      blob,
      path: fileName,
      contentType: 'image/png',
      lang,
    });

    console.log('Image uploaded successfully to S3:', url);
    return url;
  } catch (error) {
    console.error('S3 upload failed:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : 'No stack trace',
    });

    // Fallback for local development: return blob URL so modal can open
    return URL.createObjectURL(blob);
  }
}

/**
 * Конвертирует data URL в Blob
 * @param dataUrl - Data URL изображения
 * @returns Blob изображения
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}
